// Costs for billable work (PRD rev. 1.23, FR10). A QuickBooks spending line keeps its category (what
// kind of cost) and gains two facts set in Base Camp: which project it's for, and who bears it. Pure
// rules, shared by the API and the browser: how a line's coding resolves, the suggestions for lines
// not yet coded, matching expense-report repayments and rebills, and what flows on to project costs
// (ER6's path) and shared-cost actuals.
//
// QuickBooks carries nothing useful for this at 3Seven (Chris, 2026-10-28), so nothing is read from it
// beyond the line itself, and nothing is ever written back.

import { addDays, daysBetween, type Iso, type Month } from "./dates";
import { clientIdOf } from "./clients";
import { isInternalCode, type PostedExpense } from "./expense-reports";
import { type ExpenseCategory as ProjectCostCategory } from "./projects";
import { UNMAPPED, type BooksAccount, type BooksLine, type SpendCategory } from "./books";

export type CostTreatment = "company" | "project" | "billed";
export const COST_TREATMENTS: readonly CostTreatment[] = ["company", "project", "billed"];
export const TREATMENT_LABEL: Readonly<Record<CostTreatment, string>> = {
  company: "Company cost",
  project: "Project cost",
  billed: "Billed back",
};

/** One line's (or one account's) coding, as an admin set it. */
export interface CostCoding {
  code?: string;
  treatment: CostTreatment;
  by: string;
  at: string;
}

/** A billed-back line marked rebilled by hand, with the invoice it went on (FR10.4). */
export interface RebillMark {
  invoice: string;
  by: string;
  at: string;
}

export interface CodingDoc {
  /** Line coding by coding key (see codingKeyOf). */
  lines: Record<string, CostCoding>;
  /** An account's default, which its sub-accounts inherit. */
  accounts: Record<string, CostCoding>;
  rebilled: Record<string, RebillMark>;
}
export const emptyCoding = (): CodingDoc => ({ lines: {}, accounts: {}, rebilled: {} });

/**
 * The key a line's coding is kept under: its P&L line key, which is the same on the cash and accrual
 * bases (the transaction and its account), so a coding set once shows on every basis and survives
 * every sync. A bank or card line the P&L explains is coded through the P&L line it's shared from.
 */
export const codingKeyOf = (line: Pick<BooksLine, "key">): string => line.key;

/** A project as coding sees it. */
export interface CodingProject {
  code: string;
  name: string;
  clientName: string;
  internal: boolean;
  /** Open for new costs: not lost, cancelled or a template. */
  active: boolean;
  /** The contract's span, where known: a suggestion only offers a project the date falls in. */
  from?: Iso;
  to?: Iso;
}

/** Where a line's coding came from, shown beside it (FR10.1). */
export type CodingSource = "line" | "account" | "expense-report" | "default";
export const CODING_SOURCE_LABEL: Readonly<Record<CodingSource, string>> = {
  line: "Set on the line",
  account: "The account's default",
  "expense-report": "An expense report's repayment",
  default: "Not coded",
};

export interface ResolvedCoding {
  code?: string;
  treatment: CostTreatment;
  source: CodingSource;
  /** For an expense report's repayment: the report, and the share of it that's company spend (internal codes). */
  report?: { ref: string; person: string; companyShare: number };
}

/**
 * A coding as it counts. A client project's cost is never a company cost (Chris, 2026-10-28: the
 * project carries its whole program cost), so Company cost on a client code reads as Project cost;
 * an internal code (3S001–3S003) is never billed back. No project, no project cost.
 */
export function normalizeCoding(c: { code?: string; treatment: CostTreatment }): { code?: string; treatment: CostTreatment } {
  const code = c.code?.trim().toUpperCase() || undefined;
  if (!code) return { treatment: "company" };
  if (isInternalCode(code)) return { code, treatment: "project" };
  return { code, treatment: c.treatment === "billed" ? "billed" : "project" };
}

export function validateCoding(c: { code?: string; treatment?: unknown }, projects: ReadonlyMap<string, CodingProject>): string[] {
  const errors: string[] = [];
  if (c.treatment !== undefined && !COST_TREATMENTS.includes(c.treatment as CostTreatment)) errors.push("Pick company cost, project cost or billed back.");
  const code = c.code?.trim().toUpperCase();
  if (code && !projects.has(code)) errors.push(`${code} isn't a project code.`);
  if (!code && (c.treatment === "project" || c.treatment === "billed")) errors.push("A project cost or billed-back cost needs a project.");
  if (code && isInternalCode(code) && c.treatment === "billed") errors.push("An internal code can't be billed back.");
  return errors;
}

/** A line's coding (FR10.1): an expense report's repayment, else the line's own, else its account's, else Company cost. */
export function codingOf(
  line: Pick<BooksLine, "key" | "accountId">,
  accounts: ReadonlyMap<string, BooksAccount>,
  doc: CodingDoc,
  repayments?: ReadonlyMap<string, ReimbursementMatch>,
): ResolvedCoding {
  const k = codingKeyOf(line);
  const r = repayments?.get(k);
  if (r) return { treatment: "company", source: "expense-report", report: { ref: r.ref, person: r.person, companyShare: r.companyShare } };
  const own = doc.lines[k];
  if (own) return { ...normalizeCoding(own), source: "line" };
  for (let id: string | undefined = line.accountId, depth = 0; id && depth < 6; id = accounts.get(id)?.parentId, depth++) {
    const a = doc.accounts[id];
    if (a) return { ...normalizeCoding(a), source: "account" };
  }
  return { treatment: "company", source: "default" };
}

// --- Categories ------------------------------------------------------------------------------------------

/** The top-level categories whose spending is often for a project (FR10.2): travel, events, contractors. */
const PROJECT_LIKE = new Set(["travel", "events", "people-contractors"]);

export function isProjectLike(categoryId: string | undefined, categories: readonly SpendCategory[]): boolean {
  if (!categoryId) return false;
  const c = categories.find((x) => x.id === categoryId);
  return PROJECT_LIKE.has(categoryId) || (!!c?.parentId && PROJECT_LIKE.has(c.parentId));
}

/** Where a spending category lands on a project (PE8). */
export function projectCategoryOf(categoryId: string | undefined, categories: readonly SpendCategory[]): ProjectCostCategory {
  const top = categories.find((c) => c.id === categoryId)?.parentId ?? categoryId;
  if (categoryId === "people-contractors") return "subcontractor";
  if (top === "travel") return "travel";
  if (top === "events") return "event";
  if (categoryId === "office" || top === "office") return "software";
  return "other";
}

/** The shared-cost category a spending category feeds (FR10.7): its own, else its parent's. */
export function sharedCategoryOf(categoryId: string | undefined, categories: readonly SpendCategory[]): string | undefined {
  const c = categories.find((x) => x.id === categoryId);
  if (!c) return undefined;
  return c.shared ?? (c.parentId ? categories.find((x) => x.id === c.parentId)?.shared : undefined);
}

// --- Suggestions (FR10.2) ----------------------------------------------------------------------------------

const norm = (s: string | undefined) => ` ${(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;

export interface Suggestion {
  code: string;
  why: string;
}

/**
 * A suggested project for a line not yet coded: a project code named in the payee or memo; else a
 * client named there with one project open on the date; else one Base Camp person named there whose
 * client time that week was all on one project.
 */
export function suggestProject(
  line: Pick<BooksLine, "date" | "partyName" | "memo">,
  projects: readonly CodingProject[],
  people: readonly { id: string; name: string }[],
  clientCodesInWeek: (personId: string, day: Iso) => readonly string[],
): Suggestion | undefined {
  const text = norm(`${line.partyName ?? ""} ${line.memo ?? ""}`);
  const open = projects.filter((p) => p.active && !p.internal && (!p.from || p.from <= line.date) && (!p.to || addDays(p.to, 30) >= line.date));
  const byCode = projects.find((p) => p.active && text.includes(norm(p.code)));
  if (byCode) return { code: byCode.code, why: `Names ${byCode.code}` };
  const clients = [...new Set(open.map((p) => p.clientName))].filter((c) => {
    const n = norm(c);
    return n.trim().length >= 4 && (text.includes(n) || text.includes(` ${clientIdOf(c)} `));
  });
  if (clients.length === 1) {
    const theirs = open.filter((p) => p.clientName === clients[0]);
    if (theirs.length === 1) return { code: theirs[0].code, why: `Names ${clients[0]}` };
  }
  const named = people.filter((p) => {
    const n = norm(p.name);
    return n.trim().includes(" ") && text.includes(n);
  });
  if (named.length === 1) {
    const codes = [...new Set(clientCodesInWeek(named[0].id, line.date).map((c) => c.toUpperCase()))].filter((c) => !isInternalCode(c) && open.some((p) => p.code === c));
    if (codes.length === 1) return { code: codes[0], why: `${named[0].name}'s client time that week` };
  }
  return undefined;
}

// --- Expense reports repaid through QuickBooks (FR10.3) ------------------------------------------------------

export interface RepaidReport {
  id: string;
  ref: string;
  person: string;
  total: number;
  /** When it was repaid, else when it was approved. */
  on: Iso;
  /** The report's own share that is company spend: internal codes, not billed. */
  companyShare: number;
}

export interface ReimbursementMatch {
  reportId: string;
  ref: string;
  person: string;
  companyShare: number;
}

/** Days either side of the repayment date a QuickBooks payment may fall. */
export const REPAYMENT_WINDOW_DAYS = 21;

/**
 * QuickBooks lines that repay an expense report (ER4): one transaction whose lines add up to the
 * report's total, paid to the person (by name) or naming the report's reference, near the repayment
 * date. Each report matches one transaction at most. The report's own lines already count (ER6–ER8),
 * so a matched transaction adds nothing to a project; its company share still counts as shared costs.
 */
export function matchReimbursements(lines: readonly BooksLine[], reports: readonly RepaidReport[]): Map<string, ReimbursementMatch> {
  const groups = new Map<string, BooksLine[]>();
  for (const l of lines) {
    if (l.kind !== "expense" || l.transfer) continue;
    const g = l.txnId ? `txn:${l.txnId}` : codingKeyOf(l);
    groups.set(g, [...(groups.get(g) ?? []), l]);
  }
  const out = new Map<string, ReimbursementMatch>();
  const used = new Set<string>();
  for (const r of [...reports].sort((a, b) => a.on.localeCompare(b.on))) {
    const person = norm(r.person);
    const last = norm(r.person.split(/\s+/).at(-1));
    for (const [g, ls] of groups) {
      if (used.has(g)) continue;
      const sum = ls.reduce((t, l) => t + l.amount, 0);
      if (Math.abs(sum - r.total) > 0.005) continue;
      const first = ls[0];
      if (Math.abs(daysBetween(r.on, first.date)) > REPAYMENT_WINDOW_DAYS) continue;
      const text = norm(`${first.partyName ?? ""} ${first.memo ?? ""}`);
      const named = text.includes(person) || (last.trim().length >= 3 && norm(first.partyName).includes(last)) || text.includes(norm(r.ref));
      if (!named) continue;
      used.add(g);
      for (const l of ls) out.set(codingKeyOf(l), { reportId: r.id, ref: r.ref, person: r.person, companyShare: r.companyShare });
      break;
    }
  }
  return out;
}

// --- Billed back, to the invoice (FR10.4) -------------------------------------------------------------------

export interface RebillLine {
  key: string;
  date: Iso;
  amount: number;
  clientName: string;
}

export interface InvoiceLine {
  key: string;
  date: Iso;
  amount: number;
  clientName: string;
  docNumber?: string;
}

export type RebillState = { rebilled: false } | { rebilled: true; how: "matched" | "marked"; invoice: string; invoiceKey?: string };

/**
 * Whether each billed-back line has been rebilled: marked so in Base Camp with its invoice number, or
 * matched to a QuickBooks invoice line for the same client at the same amount, on or after the cost
 * (a week's grace). Each invoice line rebills one cost at most, the oldest first.
 */
export function rebillStates(costs: readonly RebillLine[], invoices: readonly InvoiceLine[], marks: Readonly<Record<string, RebillMark>>): Map<string, RebillState> {
  const out = new Map<string, RebillState>();
  const used = new Set<string>();
  for (const c of [...costs].sort((a, b) => a.date.localeCompare(b.date))) {
    const mark = marks[c.key];
    if (mark) {
      out.set(c.key, { rebilled: true, how: "marked", invoice: mark.invoice });
      continue;
    }
    const client = clientIdOf(c.clientName);
    const inv = invoices
      .filter((i) => !used.has(i.key) && clientIdOf(i.clientName) === client && Math.abs(i.amount - c.amount) <= 0.005 && i.date >= addDays(c.date, -7))
      .sort((a, b) => a.date.localeCompare(b.date))[0];
    if (inv) {
      used.add(inv.key);
      out.set(c.key, { rebilled: true, how: "matched", invoice: inv.docNumber ?? inv.date, invoiceKey: inv.key });
    } else out.set(c.key, { rebilled: false });
  }
  return out;
}

// --- What flows on: project costs (ER6's path) and shared-cost actuals (FR10.7) ---------------------------

/** A QuickBooks spending line with its category and coding resolved. */
export interface CodedLine extends BooksLine {
  categoryId: string;
  coding: ResolvedCoding;
}

const nextMonth = (m: Month): Month => {
  const [y, mo] = m.split("-").map(Number);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
};

/**
 * Project costs from QuickBooks, shaped as approved expense lines so Performance and Profitability
 * count them the same way (ER6, AC7): client-project lines treated as Project cost. Billed back never
 * moves margin (ER8); internal codes are shared costs (ER7); a repaid expense report already counts.
 */
export function booksProjectCosts(lines: readonly CodedLine[], categories: readonly SpendCategory[], closedMonths: ReadonlySet<string>): PostedExpense[] {
  const out: PostedExpense[] = [];
  for (const l of lines) {
    if (l.kind !== "expense" || l.transfer || l.coding.treatment !== "project" || !l.coding.code || isInternalCode(l.coding.code)) continue;
    const own = l.date.slice(0, 7);
    let month = own;
    while (closedMonths.has(month)) month = nextMonth(month);
    out.push({
      reportId: "quickbooks",
      lineId: codingKeyOf(l),
      personId: "",
      code: l.coding.code,
      month,
      late: month !== own,
      date: l.date,
      amount: l.amount,
      billable: false,
      internal: false,
      categoryId: l.categoryId,
      projectCategory: projectCategoryOf(l.categoryId, categories),
      sharedCategory: sharedCategoryOf(l.categoryId, categories) ?? "other",
    });
  }
  return out;
}

/**
 * Shared-cost actuals from QuickBooks (FR10.7), by month and shared-cost category, for months before
 * `before`: spending that is a company cost (no project, or an internal code), not People, in a
 * category mapped to a shared-cost category. A repaid expense report counts its company share.
 */
export function sharedActualsFromBooks(lines: readonly CodedLine[], categories: readonly SpendCategory[], before: Month): Map<Month, Map<string, number>> {
  const out = new Map<Month, Map<string, number>>();
  for (const l of lines) {
    if (l.kind !== "expense" || l.transfer || l.categoryId === UNMAPPED) continue;
    const month = l.date.slice(0, 7);
    if (month >= before) continue;
    const top = categories.find((c) => c.id === l.categoryId)?.parentId ?? l.categoryId;
    if (top === "people") continue;
    const shared = sharedCategoryOf(l.categoryId, categories);
    if (!shared) continue;
    const c = l.coding;
    const share = c.source === "expense-report" ? (c.report?.companyShare ?? 0) : c.treatment === "company" || (c.treatment === "project" && !!c.code && isInternalCode(c.code)) ? 1 : 0;
    if (!share) continue;
    const byCat = out.get(month) ?? new Map<string, number>();
    byCat.set(shared, (byCat.get(shared) ?? 0) + l.amount * share);
    out.set(month, byCat);
  }
  return out;
}
